import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'

export enum AIPlanCreateStep {
  SelectBasis = 'SelectBasis',
  OutputFormat = 'OutputFormat',
  Channels = 'Channels',
  Personas = 'Personas',
  Timeframe = 'Timeframe',
  AdditionalGuidance = 'AdditionalGuidance'
}

export enum OutputFormatOption {
  ThreePlansThreeBudgets = 'ThreePlansThreeBudgets',
  ThreePlansOneBudget = 'ThreePlansOneBudget',
  OnePlan = 'OnePlan'
}

export enum BudgetModeOption {
  AiRecommended = 'AiRecommended',
  Custom = 'Custom'
}

export enum ChannelModeOption {
  AiRecommended = 'AiRecommended',
  Specific = 'Specific'
}

export enum PersonaModeOption {
  All = 'All',
  Selected = 'Selected',
  Ignore = 'Ignore'
}

export enum TimeframeOption {
  TwelveMonths = 'TwelveMonths',
  SixMonths = 'SixMonths',
  Other = 'Other'
}

export interface CreateAIPlanState {
  step: AIPlanCreateStep
  blueprintId: string
  outputFormat: OutputFormatOption
  budgetMode: BudgetModeOption
  monthlyBudget?: number
  highBudget?: number
  mediumBudget?: number
  lowBudget?: number
  channelMode: ChannelModeOption
  selectedChannels: string[]
  personaMode: PersonaModeOption
  selectedPersonas: string[]
  timeframeOption: TimeframeOption
  customDurationMonths?: number
  planStartDate: string
  additionalGuidance: string
}

const initialState: CreateAIPlanState = {
  step: AIPlanCreateStep.SelectBasis,
  blueprintId: '',
  outputFormat: OutputFormatOption.ThreePlansThreeBudgets,
  budgetMode: BudgetModeOption.AiRecommended,
  monthlyBudget: undefined,
  highBudget: undefined,
  mediumBudget: undefined,
  lowBudget: undefined,
  channelMode: ChannelModeOption.AiRecommended,
  selectedChannels: [],
  personaMode: PersonaModeOption.All,
  selectedPersonas: [],
  timeframeOption: TimeframeOption.TwelveMonths,
  customDurationMonths: undefined,
  planStartDate: moment().add(1, 'month').format('MM/DD/YYYY'),
  additionalGuidance: ''
}

const createAIPlanSlice = createSlice({
  name: 'createAIPlan',
  initialState,
  reducers: {
    setStep(state: CreateAIPlanState, { payload }: PayloadAction<AIPlanCreateStep>) {
      state.step = payload
    },
    setBlueprintId(state: CreateAIPlanState, { payload }: PayloadAction<string>) {
      state.blueprintId = payload
    },
    setOutputFormat(state: CreateAIPlanState, { payload }: PayloadAction<OutputFormatOption>) {
      state.outputFormat = payload
    },
    setBudgetMode(state: CreateAIPlanState, { payload }: PayloadAction<BudgetModeOption>) {
      state.budgetMode = payload
    },
    setMonthlyBudget(state: CreateAIPlanState, { payload }: PayloadAction<number | undefined>) {
      state.monthlyBudget = payload
    },
    setHighBudget(state: CreateAIPlanState, { payload }: PayloadAction<number | undefined>) {
      state.highBudget = payload
    },
    setMediumBudget(state: CreateAIPlanState, { payload }: PayloadAction<number | undefined>) {
      state.mediumBudget = payload
    },
    setLowBudget(state: CreateAIPlanState, { payload }: PayloadAction<number | undefined>) {
      state.lowBudget = payload
    },
    setChannelMode(state: CreateAIPlanState, { payload }: PayloadAction<ChannelModeOption>) {
      state.channelMode = payload
    },
    setSelectedChannels(state: CreateAIPlanState, { payload }: PayloadAction<string[]>) {
      state.selectedChannels = payload
    },
    setPersonaMode(state: CreateAIPlanState, { payload }: PayloadAction<PersonaModeOption>) {
      state.personaMode = payload
    },
    setSelectedPersonas(state: CreateAIPlanState, { payload }: PayloadAction<string[]>) {
      state.selectedPersonas = payload
    },
    setTimeframeOption(state: CreateAIPlanState, { payload }: PayloadAction<TimeframeOption>) {
      state.timeframeOption = payload
    },
    setCustomDurationMonths(state: CreateAIPlanState, { payload }: PayloadAction<number | undefined>) {
      state.customDurationMonths = payload
    },
    setPlanStartDate(state: CreateAIPlanState, { payload }: PayloadAction<moment.Moment>) {
      state.planStartDate = payload.format('MM/DD/YYYY')
    },
    setAdditionalGuidance(state: CreateAIPlanState, { payload }: PayloadAction<string>) {
      state.additionalGuidance = payload
    },
    reset(state: CreateAIPlanState) {
      state.step = initialState.step
      state.blueprintId = initialState.blueprintId
      state.outputFormat = initialState.outputFormat
      state.budgetMode = initialState.budgetMode
      state.monthlyBudget = initialState.monthlyBudget
      state.highBudget = initialState.highBudget
      state.mediumBudget = initialState.mediumBudget
      state.lowBudget = initialState.lowBudget
      state.channelMode = initialState.channelMode
      state.selectedChannels = initialState.selectedChannels
      state.personaMode = initialState.personaMode
      state.selectedPersonas = initialState.selectedPersonas
      state.timeframeOption = initialState.timeframeOption
      state.customDurationMonths = initialState.customDurationMonths
      state.planStartDate = initialState.planStartDate
      state.additionalGuidance = initialState.additionalGuidance
    }
  }
})

export const {
  setStep,
  setBlueprintId,
  setOutputFormat,
  setBudgetMode,
  setMonthlyBudget,
  setHighBudget,
  setMediumBudget,
  setLowBudget,
  setChannelMode,
  setSelectedChannels,
  setPersonaMode,
  setSelectedPersonas,
  setTimeframeOption,
  setCustomDurationMonths,
  setPlanStartDate,
  setAdditionalGuidance,
  reset
} = createAIPlanSlice.actions

export default createAIPlanSlice.reducer
